<script setup lang="ts">
import { VIcon } from "@app/core";
import { computed } from "vue";

const { query, total, active } = defineProps<{
  query: string
  total: number
  active: boolean
}>();

const emit = defineEmits<{
  select: []
  "update:active": []
}>();

const label = computed(() =>
  total > 0
    ? `See all ${total} results for`
    : "Search all products for",
);
</script>

<template>
  <button
    :class="{ 'search-all-item--active': active }"
    class="search-all-item"
    type="button"
    @click="emit('select')"
    @mouseenter="emit('update:active')"
  >
    <span class="search-all-item__icon">
      <VIcon
        icon="lucide:arrow-right"
        :size="15"
      />
    </span>

    <span class="search-all-item__label">
      {{ label }}
      <strong class="search-all-item__query">"{{ query }}"</strong>
    </span>

    <VIcon
      class="search-all-item__arrow"
      icon="lucide:chevron-right"
      :size="14"
    />
  </button>
</template>

<style scoped>
.search-all-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 100ms ease;
}

@media (hover: hover) and (pointer: fine) {
  .search-all-item:hover {
    background-color: rgb(var(--color-primary, 14 165 233) / 0.06);
  }
}

.search-all-item--active {
  background-color: rgb(var(--color-primary, 14 165 233) / 0.08);
}

.search-all-item:active {
  transform: scale(0.99);
  transition: transform 100ms ease;
}

.search-all-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2rem;
  border-radius: 0.375rem;
  background-color: rgb(var(--color-primary, 14 165 233) / 0.1);
  color: rgb(var(--color-primary, 14 165 233) / 1);
  flex-shrink: 0;
}

.search-all-item__label {
  flex: 1;
  font-size: 0.8125rem;
  color: rgb(var(--color-mainText, 30 41 59) / 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-all-item__query {
  color: rgb(var(--color-mainText, 30 41 59) / 1);
  font-weight: 600;
}

.search-all-item__arrow {
  color: rgb(var(--color-secondaryText, 100 116 139) / 0.5);
  flex-shrink: 0;
}
</style>
